import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Trash2 } from "lucide-react";
import { api } from "@/api/client";
import type { ScheduleCadence, ScheduleWithStudy } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { formatTimesPerDay } from "@/lib/format";

const CADENCE_OPTIONS: { value: ScheduleCadence; label: string }[] = [
  { value: "hourly", label: "Hourly" },
  { value: "times_per_day", label: "Several times a day" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function formatCadence(s: { cadence: ScheduleCadence; hour_utc: number; day_of_week: number | null; day_of_month: number | null; times_per_day?: number | null }): string {
  const hour = `${String(s.hour_utc).padStart(2, "0")}:00 UTC`;
  switch (s.cadence) {
    case "hourly": return "Every hour";
    case "times_per_day": return formatTimesPerDay(s.times_per_day ?? 2);
    case "daily": return `Daily at ${hour}`;
    case "weekly": return `${WEEKDAYS[s.day_of_week ?? 1]}s at ${hour}`;
    case "monthly": return `Day ${s.day_of_month ?? 1} of each month at ${hour}`;
  }
}

function formatNextRun(sqlDatetime: string): string {
  const date = new Date(sqlDatetime.replace(" ", "T") + "Z");
  return date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

interface SchedulePanelProps {
  studyId: number;
}

/**
 * Recurring-run controls for one study: create a cadence, pause/resume it, or
 * remove it. Shows the next run time and a rough LLM-calls-per-month estimate
 * so heavy cadences are a visible choice rather than a surprise bill.
 */
export function SchedulePanel({ studyId }: SchedulePanelProps) {
  const [schedule, setSchedule] = useState<ScheduleWithStudy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state for creating a schedule
  const [cadence, setCadence] = useState<ScheduleCadence>("daily");
  const [hourUtc, setHourUtc] = useState(9);
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timesPerDay, setTimesPerDay] = useState(4);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.schedules.list()
      .then(all => { if (!cancelled) setSchedule(all.find(s => s.study_id === studyId) ?? null); })
      .catch(err => { if (!cancelled) setError(err?.message || "Failed to load schedule"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studyId]);

  const withSaving = async (action: () => Promise<void>) => {
    setSaving(true);
    setError("");
    try {
      await action();
    } catch (err: any) {
      setError(err?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = () => withSaving(async () => {
    const created = await api.schedules.create({
      study_id: studyId,
      cadence,
      hour_utc: hourUtc,
      day_of_week: cadence === "weekly" ? dayOfWeek : undefined,
      day_of_month: cadence === "monthly" ? dayOfMonth : undefined,
      times_per_day: cadence === "times_per_day" ? timesPerDay : undefined,
    });
    setSchedule(created);
  });

  const handleToggle = () => withSaving(async () => {
    if (!schedule) return;
    setSchedule(await api.schedules.update(schedule.id, { enabled: !schedule.enabled }));
  });

  const handleDelete = () => withSaving(async () => {
    if (!schedule) return;
    await api.schedules.delete(schedule.id);
    setSchedule(null);
  });

  if (loading) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-brand-600" />
              Schedule
            </CardTitle>
            <CardDescription>
              {schedule
                ? "This study runs automatically and builds a trend history."
                : "Run this study on a cadence to track visibility and sentiment over time."}
            </CardDescription>
          </div>
          {schedule && (
            <Badge variant={schedule.enabled ? "success" : "muted"}>
              {schedule.enabled ? "Active" : "Paused"}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {schedule ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[13px]">
              <span className="font-medium text-brand-950">{formatCadence(schedule)}</span>
              {schedule.enabled && (
                <span className="text-slate-500">Next run: {formatNextRun(schedule.next_run_at)}</span>
              )}
              {schedule.last_run_at && (
                <span className="text-slate-400">Last run: {formatNextRun(schedule.last_run_at)}</span>
              )}
            </div>
            <p className="text-[12px] text-slate-400">
              ~{schedule.estimated_calls_per_month.toLocaleString()} LLM calls/month at this cadence
            </p>
            <div className="flex gap-2 pt-1">
              <Button variant="secondary" size="sm" onClick={handleToggle} disabled={saving}>
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                {schedule.enabled ? "Pause" : "Resume"}
              </Button>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-600" onClick={handleDelete} disabled={saving}>
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="schedule-cadence">Cadence</Label>
                <NativeSelect id="schedule-cadence" value={cadence} onChange={e => setCadence(e.target.value as ScheduleCadence)}>
                  {CADENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </NativeSelect>
              </div>
              {cadence === "weekly" && (
                <div>
                  <Label htmlFor="schedule-dow">Day of Week</Label>
                  <NativeSelect id="schedule-dow" value={dayOfWeek} onChange={e => setDayOfWeek(Number(e.target.value))}>
                    {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </NativeSelect>
                </div>
              )}
              {cadence === "monthly" && (
                <div>
                  <Label htmlFor="schedule-dom">Day of Month</Label>
                  <NativeSelect id="schedule-dom" value={dayOfMonth} onChange={e => setDayOfMonth(Number(e.target.value))}>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                  </NativeSelect>
                </div>
              )}
              {cadence === "times_per_day" && (
                <div>
                  <Label htmlFor="schedule-tpd">Runs per Day</Label>
                  <NativeSelect id="schedule-tpd" value={timesPerDay} onChange={e => setTimesPerDay(Number(e.target.value))}>
                    {Array.from({ length: 23 }, (_, i) => i + 2).map(n => (
                      <option key={n} value={n}>{formatTimesPerDay(n)}</option>
                    ))}
                  </NativeSelect>
                </div>
              )}
              {cadence !== "hourly" && cadence !== "times_per_day" && (
                <div>
                  <Label htmlFor="schedule-hour">Time (UTC)</Label>
                  <NativeSelect id="schedule-hour" value={hourUtc} onChange={e => setHourUtc(Number(e.target.value))}>
                    {Array.from({ length: 24 }, (_, i) => i).map(h => (
                      <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                    ))}
                  </NativeSelect>
                </div>
              )}
            </div>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />}
              Start Schedule
            </Button>
          </div>
        )}
        {error && <p className="mt-3 text-[12px] text-red-600">{error}</p>}
      </CardContent>
    </Card>
  );
}
